import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Plus, Search, Package, Tag, Pencil, Trash2, ShoppingBag, Eye, Upload, Image, X,
  ChevronLeft, ChevronRight, Share2, ExternalLink, Copy, Download, BarChart3,
  Linkedin, Facebook, Mail, MessageCircle, Play, Pause, Maximize2, Minimize2,
  TrendingUp, MousePointerClick, Globe, Sparkles, Video, Send, Bookmark, Phone,
  PhoneCall, Instagram, Headphones, Calendar
} from 'lucide-react';
import { ViewToggle } from '../components/ui/view-toggle';
import axios from 'axios';
import { useCurrency } from '../contexts/CurrencyContext';
import { formatCurrency as fmtCurrencyGlobal } from '../utils/locale';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { MiniTour } from '../components/MiniTour';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'product', label: 'Product', color: 'bg-blue-100 text-blue-700' },
  { value: 'service', label: 'Service', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'booking', label: 'Booking', color: 'bg-teal-100 text-teal-700' },
  { value: 'subscription', label: 'Subscription', color: 'bg-amber-100 text-amber-700' },
  { value: 'membership', label: 'Membership', color: 'bg-pink-100 text-pink-700' },
  { value: 'digital_download', label: 'Digital Download', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'bundle', label: 'Bundle', color: 'bg-purple-100 text-purple-700' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-600' },
];

function formatCurrency(val) {
  return fmtCurrencyGlobal(val || 0);
}

function formatUnitLabel(unit, compact = false) {
  if (!unit || unit === 'each') return '';
  if (compact) {
    const map = { project: '/project', hour: '/hr', month: '/mo', year: '/yr' };
    return map[unit] || `/${unit}`;
  }
  const map = { project: 'per project', hour: 'per hour', month: 'per month', year: 'per year' };
  return map[unit] || `per ${unit}`;
}

// ─── Holographic Media Carousel ───
function HoloCarousel({ images = [], videoUrl = '', onFullscreen }) {
  const [idx, setIdx] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const containerRef = useRef(null);

  const allMedia = [
    ...images.map(url => ({ type: 'image', url })),
    ...(videoUrl ? [{ type: 'video', url: videoUrl }] : []),
  ];
  const total = allMedia.length;

  const goNext = () => setIdx(i => (i + 1) % total);
  const goPrev = () => setIdx(i => (i - 1 + total) % total);

  const handleTouchStart = (e) => { setIsDragging(true); setDragStartX(e.touches?.[0]?.clientX || e.clientX); };
  const handleTouchEnd = (e) => {
    if (!isDragging) return;
    const endX = e.changedTouches?.[0]?.clientX || e.clientX;
    const diff = dragStartX - endX;
    if (Math.abs(diff) > 50) { diff > 0 ? goNext() : goPrev(); }
    setIsDragging(false);
  };

  const getVideoEmbedUrl = (url) => {
    if (!url) return '';
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=0&rel=0`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  };

  if (total === 0) {
    return (
      <div className="relative w-full aspect-[16/10] flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Package className="h-20 w-20 text-slate-700" />
      </div>
    );
  }

  const current = allMedia[idx];

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[16/10] overflow-hidden bg-gradient-to-br from-slate-950 via-[#0a1628] to-slate-950 group"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleTouchStart}
      onMouseUp={handleTouchEnd}
      data-testid="holo-carousel"
    >
      {/* Depth layers — ambient glow behind media */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] rounded-[40%] bg-emerald-500/[0.04] blur-[80px]" />
        <div className="absolute top-1/4 right-1/4 w-40 h-40 rounded-full bg-blue-500/[0.06] blur-[60px]" />
        <div className="absolute bottom-1/4 left-1/3 w-32 h-32 rounded-full bg-purple-500/[0.05] blur-[50px]" />
      </div>

      {/* The "holographic" media — floating with depth */}
      <div className="absolute inset-0 flex items-center justify-center perspective-[1200px]">
        <div
          className="relative w-[92%] h-[92%] sm:w-[85%] sm:h-[85%] transition-all duration-700 ease-out"
          style={{
            transform: `rotateY(${isDragging ? '2deg' : '0deg'}) translateZ(20px)`,
            filter: 'drop-shadow(0 0 40px rgba(16,185,129,0.15)) drop-shadow(0 25px 50px rgba(0,0,0,0.5))',
          }}
        >
          {current.type === 'image' ? (
            <img
              src={current.url}
              alt=""
              className="w-full h-full object-contain select-none"
              draggable={false}
              style={{
                maskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 60%, transparent 100%)',
                WebkitMaskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 60%, transparent 100%)',
              }}
            />
          ) : (
            <div className="w-full h-full rounded-xl overflow-hidden"
              style={{
                maskImage: 'radial-gradient(ellipse 92% 92% at 50% 50%, black 65%, transparent 100%)',
                WebkitMaskImage: 'radial-gradient(ellipse 92% 92% at 50% 50%, black 65%, transparent 100%)',
              }}
            >
              <iframe
                src={getVideoEmbedUrl(current.url)}
                className="w-full h-full border-0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Product Video"
              />
            </div>
          )}
        </div>
      </div>

      {/* Reflection effect */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent pointer-events-none" />

      {/* Navigation arrows */}
      {total > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/80 hover:bg-white/20 hover:text-white transition-all sm:opacity-0 sm:group-hover:opacity-100 z-10"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/80 hover:bg-white/20 hover:text-white transition-all sm:opacity-0 sm:group-hover:opacity-100 z-10"
            data-testid="carousel-next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      {/* Dot indicators */}
      {total > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {allMedia.map((m, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={cn(
                "h-1.5 rounded-full transition-all duration-300",
                i === idx
                  ? "w-6 bg-emerald-400"
                  : "w-1.5 bg-white/30 hover:bg-white/50"
              )}
            />
          ))}
        </div>
      )}

      {/* Fullscreen button */}
      {onFullscreen && (
        <button
          onClick={onFullscreen}
          className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-white/10 backdrop-blur-md flex items-center justify-center text-white/70 hover:bg-white/20 hover:text-white transition-all opacity-0 group-hover:opacity-100 z-10"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
      )}

      {/* Media type badge */}
      {current.type === 'video' && (
        <div className="absolute top-3 left-3 z-10">
          <Badge className="bg-red-500/80 text-white text-[10px] gap-1 backdrop-blur-md"><Play className="h-3 w-3" />Video</Badge>
        </div>
      )}
    </div>
  );
}


// ─── Fullscreen Immersive Preview ───
function ImmersivePreview({ open, onClose, product, onShare, onContact }) {
  const [activeTab, setActiveTab] = useState('showcase');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (open && product?.id) {
      axios.get(`${API}/api/sales/products/${product.id}/stats`).then(r => setStats(r.data)).catch(() => {});
    }
  }, [open, product?.id]);

  if (!product) return null;
  const cat = CATEGORIES.find(c => c.value === product.category) || CATEGORIES[4];
  const images = product.image_urls || [];
  const totalShares = stats ? Object.values(stats.shares || {}).reduce((a, b) => a + (b || 0), 0) : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[95vh] sm:h-[90vh] p-0 overflow-hidden bg-slate-950 border-slate-800/50 [&>button]:text-white [&>button]:hover:text-white" data-testid="immersive-preview">

        {/* Top bar — clean, minimal on mobile */}
        <div className="sticky top-0 z-20 flex items-center justify-between px-3 sm:px-5 py-2.5 bg-slate-950 border-b border-slate-800/50">
          <Badge className={`${cat.color} text-[10px] flex-shrink-0`}>{cat.label}</Badge>
          <div className="flex items-center gap-0.5 sm:gap-2 flex-shrink-0">
            {stats && (
              <div className="hidden sm:flex items-center gap-3 text-[11px] text-slate-400 mr-3">
                <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{stats.views}</span>
                <span className="flex items-center gap-1"><Share2 className="h-3 w-3" />{totalShares}</span>
                <span className="flex items-center gap-1"><Download className="h-3 w-3" />{stats.downloads}</span>
              </div>
            )}
            <Button variant="ghost" size="icon" className="text-white/70 hover:text-white h-8 w-8 sm:h-9 sm:w-auto sm:px-3 sm:gap-1.5" onClick={() => onShare?.(product)} data-testid="share-from-preview">
              <Share2 className="h-4 w-4" /><span className="hidden sm:inline text-xs">Share</span>
            </Button>
            <Button variant="ghost" size="icon" className="text-emerald-400/80 hover:text-emerald-300 h-8 w-8 sm:h-9 sm:w-auto sm:px-3 sm:gap-1.5" onClick={() => onContact?.(product)} data-testid="contact-from-preview">
              <Phone className="h-4 w-4" /><span className="hidden sm:inline text-xs">Contact</span>
            </Button>
          </div>
        </div>

        {/* Content — scrollable on mobile, side-by-side on desktop */}
        <div className="overflow-y-auto lg:overflow-hidden lg:flex lg:flex-row" style={{ maxHeight: 'calc(95vh - 48px)' }}>
          {/* Left: Media — 16:9 on mobile, flex on desktop */}
          <div className="flex-1 min-h-0 flex items-center justify-center lg:overflow-hidden">
            <div className="w-full aspect-video lg:aspect-auto lg:h-full">
              <HoloCarousel images={images} videoUrl={product.video_url} />
            </div>
          </div>

          {/* Right: Details panel */}
          <div className="w-full lg:w-80 xl:w-96 flex-shrink-0 bg-slate-900/80 backdrop-blur-xl lg:border-l border-t lg:border-t-0 border-slate-800/50 lg:overflow-y-auto">
            <div className="p-4 sm:p-5 space-y-4 sm:space-y-5">
              {/* Title + Price card */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20">
                <h2 className="text-lg sm:text-xl font-bold text-white leading-snug mb-2">{product.name}</h2>
                <p className="text-2xl sm:text-3xl font-bold text-emerald-400">{formatCurrency(product.price)}</p>
                {formatUnitLabel(product.unit) && <p className="text-xs text-emerald-400/60 mt-0.5">{formatUnitLabel(product.unit)}</p>}
              </div>

              {/* Description */}
              {product.description && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-medium">Description</p>
                  <p className="text-sm text-slate-300 leading-relaxed">{product.description}</p>
                </div>
              )}

              {/* Tags */}
              {(product.tags || []).length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                  {product.tags.map(t => (
                    <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700/50">{t}</span>
                  ))}
                </div>
              )}

              {/* Meta */}
              <div className="space-y-2 text-xs">
                {product.sku && (
                  <div className="flex justify-between text-slate-500">
                    <span>SKU</span><span className="font-mono text-slate-400">{product.sku}</span>
                  </div>
                )}
                <div className="flex justify-between text-slate-500">
                  <span>Status</span>
                  <Badge className={product.active ? 'bg-emerald-500/20 text-emerald-400 text-[10px]' : 'bg-red-500/20 text-red-400 text-[10px]'}>
                    {product.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>

              {/* Stats */}
              {stats && (stats.views > 0 || totalShares > 0) && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-medium flex items-center gap-1"><BarChart3 className="h-3 w-3" />Analytics</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2.5 rounded-lg bg-slate-800/60 text-center">
                      <p className="text-lg font-bold text-white">{stats.views}</p>
                      <p className="text-[9px] text-slate-500">Views</p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-slate-800/60 text-center">
                      <p className="text-lg font-bold text-emerald-400">{totalShares}</p>
                      <p className="text-[9px] text-slate-500">Shares</p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-slate-800/60 text-center">
                      <p className="text-lg font-bold text-blue-400">{stats.downloads}</p>
                      <p className="text-[9px] text-slate-500">Downloads</p>
                    </div>
                  </div>

                  {/* Share breakdown */}
                  {totalShares > 0 && (
                    <div className="mt-3 space-y-1">
                      {Object.entries(stats.shares || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([platform, count]) => (
                        <div key={platform} className="flex items-center justify-between text-[11px]">
                          <span className="capitalize text-slate-400">{platform}</span>
                          <span className="text-slate-300 font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile bottom CTA bar */}
        <div className="lg:hidden sticky bottom-0 z-20 flex gap-2 p-3 bg-slate-950 border-t border-slate-800/50">
          <Button className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white gap-2 h-10 text-sm" onClick={() => onContact?.(product)} data-testid="mobile-contact-btn">
            <Phone className="h-4 w-4" /> Contact Us
          </Button>
          <Button variant="outline" className="flex-1 border-slate-700 text-white/70 hover:text-white hover:bg-slate-800 gap-2 h-10 text-sm" onClick={() => onShare?.(product)} data-testid="mobile-share-btn">
            <Share2 className="h-4 w-4" /> Share
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─── Social Share Modal ───
function ShareModal({ open, onClose, product }) {
  const [shareLink, setShareLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open && product?.id) {
      setLoading(true);
      axios.post(`${API}/api/sales/products/${product.id}/share-link`)
        .then(r => {
          const token = r.data.share_token;
          setShareLink(`${window.location.origin}/showcase/${token}`);
        })
        .catch(() => toast.error('Unable to generate share link'))
        .finally(() => setLoading(false));
    }
  }, [open, product?.id]);

  const trackAndOpen = (platform, url, p) => {
    const token = shareLink.split('/showcase/')[1];
    if (token) {
      axios.post(`${API}/api/sales/products/public/${token}/track?platform=${platform}&event=share`).catch(() => {});
    }
    // Instagram/TikTok: copy link + toast (no direct share API)
    if (p?.action === 'copy') {
      navigator.clipboard.writeText(ogUrl);
      toast.success(`Link copied! Paste it in ${p.label}`);
      return;
    }
    // Messenger: try deep link, fallback to Facebook sharer
    if (p?.fallback) {
      const w = window.open(url, '_blank');
      setTimeout(() => { if (!w || w.closed) window.open(p.fallback, '_blank', 'noopener,noreferrer,width=600,height=500'); }, 500);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=500');
  };

  // OG URL for social crawlers — cache-buster forces re-crawl after code changes
  const ogToken = shareLink ? shareLink.split('/showcase/')[1] : '';
  const ogUrl = ogToken ? `${window.location.origin}/api/sales/products/og/${ogToken}?v=3` : '';
  const shareText = product ? `${product.name} — ${formatCurrency(product.price)}` : '';
  const encodedOgUrl = encodeURIComponent(ogUrl);
  const encodedDirectUrl = encodeURIComponent(shareLink);
  // WhatsApp/Messenger: URL on its own line triggers rich link preview
  const waText = encodeURIComponent(`${shareText}\n${ogUrl}`);

  const platforms = [
    { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: 'bg-[#0A66C2] hover:bg-[#004182]', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedOgUrl}` },
    { id: 'facebook', label: 'Facebook', icon: Facebook, color: 'bg-[#1877F2] hover:bg-[#0d65d9]', url: `https://www.facebook.com/sharer/sharer.php?u=${encodedOgUrl}` },
    { id: 'twitter', label: 'X / Twitter', icon: Globe, color: 'bg-black hover:bg-gray-800', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodedOgUrl}` },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'bg-[#25D366] hover:bg-[#1da851]', url: `https://api.whatsapp.com/send?text=${waText}` },
    { id: 'messenger', label: 'Messenger', icon: MessageCircle, color: 'bg-[#006AFF] hover:bg-[#0054cc]', url: `https://www.facebook.com/sharer/sharer.php?u=${encodedOgUrl}&t=${encodeURIComponent(shareText)}` },
    { id: 'instagram', label: 'Instagram', icon: Globe, color: 'bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90', action: 'copy' },
    { id: 'tiktok', label: 'TikTok', icon: Globe, color: 'bg-black hover:bg-gray-800', action: 'copy' },
    { id: 'reddit', label: 'Reddit', icon: Globe, color: 'bg-[#FF4500] hover:bg-[#d93b00]', url: `https://www.reddit.com/submit?url=${encodedOgUrl}&title=${encodeURIComponent(shareText)}` },
    { id: 'email', label: 'Email', icon: Mail, color: 'bg-gray-600 hover:bg-gray-700', url: `mailto:?subject=${encodeURIComponent(product?.name || '')}&body=${encodeURIComponent(shareText + '\n\n' + ogUrl)}` },
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(ogUrl);
    setCopied(true);
    const token = shareLink.split('/showcase/')[1];
    if (token) axios.post(`${API}/api/sales/products/public/${token}/track?platform=copy&event=share`).catch(() => {});
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copied to clipboard');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-slate-950 border-slate-800 text-white" data-testid="share-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white"><Share2 className="h-5 w-5 text-emerald-400" />Share Product</DialogTitle>
          <DialogDescription className="text-slate-400">Share &ldquo;{product?.name}&rdquo; across platforms</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Copy link */}
          <div className="flex gap-2">
            <Input
              value={loading ? 'Generating...' : ogUrl}
              readOnly
              className="bg-slate-900 border-slate-700 text-slate-300 text-xs"
              data-testid="share-link-input"
            />
            <Button onClick={handleCopy} disabled={loading} className="shrink-0 bg-emerald-600 hover:bg-emerald-500 gap-1 text-xs" data-testid="copy-share-link">
              {copied ? 'Copied!' : <><Copy className="h-3.5 w-3.5" />Copy</>}
            </Button>
          </div>

          {/* Platform buttons */}
          <div className="grid grid-cols-3 gap-2">
            {platforms.map(p => (
              <button
                key={p.id}
                onClick={() => trackAndOpen(p.id, p.url, p)}
                disabled={loading}
                className={cn(
                  "flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-white text-xs font-medium transition-all",
                  p.color
                )}
                data-testid={`share-${p.id}`}
              >
                <p.icon className="h-4 w-4" />{p.label}
              </button>
            ))}
          </div>

          <p className="text-[10px] text-slate-600 text-center">
            Powered by Konnect - Connecting Pieces of the Business Puzzle
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─── Product Form Modal ───
function ProductModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({ name: '', description: '', category: 'service', price: '', unit: 'project', sku: '', active: true, tags: '', image_urls: [], video_url: '', booking_service_id: '', has_promo: false, promo_label: '', promo_type: 'percentage', promo_value: '', promo_start: '', promo_end: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bookingServices, setBookingServices] = useState([]);

  useEffect(() => {
    if (editing) {
      setForm({ ...editing, price: editing.price?.toString() || '', tags: (editing.tags || []).join(', '), image_urls: editing.image_urls || [], video_url: editing.video_url || '', booking_service_id: editing.booking_service_id || '', has_promo: editing.has_promo || false, promo_label: editing.promo_label || '', promo_type: editing.promo_type || 'percentage', promo_value: editing.promo_value?.toString() || '', promo_start: editing.promo_start || '', promo_end: editing.promo_end || '' });
    } else {
      setForm({ name: '', description: '', category: 'service', price: '', unit: 'project', sku: '', active: true, tags: '', image_urls: [], video_url: '', booking_service_id: '', has_promo: false, promo_label: '', promo_type: 'percentage', promo_value: '', promo_start: '', promo_end: '' });
    }
  }, [editing, open]);

  // Load bookable services once when the user selects Booking category — enables the
  // linked-service dropdown so a product row can point at a specific bookable slot,
  // creating a seamless bridge between Products & Services and the Booking module.
  useEffect(() => {
    if (form.category !== 'booking' || bookingServices.length > 0) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/booking/services`);
        setBookingServices(Array.isArray(data) ? data : []);
      } catch {
        setBookingServices([]);
      }
    })();
  }, [form.category, bookingServices.length]);

  // When a booking service is picked, auto-fill name/price/unit if blank so the
  // operator does not have to retype data that already exists on the booking side.
  const applyBookingService = (bsId) => {
    const bs = bookingServices.find((x) => x.id === bsId);
    if (!bs) { setForm((p) => ({ ...p, booking_service_id: bsId })); return; }
    setForm((p) => ({
      ...p,
      booking_service_id: bsId,
      name: p.name || bs.name,
      price: p.price || (bs.price_min ? String(bs.price_min) : ''),
      unit: 'each',
      description: p.description || (bs.duration_min ? `${bs.duration_min}-minute session` : ''),
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await axios.post(`${API}/api/qr/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        uploaded.push(data.url);
      }
      setForm(f => ({ ...f, image_urls: [...f.image_urls, ...uploaded] }));
      toast.success(`${uploaded.length} image(s) uploaded`);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const removeImage = (idx) => setForm(f => ({ ...f, image_urls: f.image_urls.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Product name is required.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [], promo_value: parseFloat(form.promo_value) || 0 };
      const { data } = editing
        ? await axios.put(`${API}/api/sales/products/${editing.id}`, payload)
        : await axios.post(`${API}/api/sales/products`, payload);
      toast.success(editing ? 'Product updated successfully' : 'Product created successfully');
      onSaved(data, !!editing);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Operation failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-orange-500" />{editing ? 'Edit' : 'Add'} Product</DialogTitle><DialogDescription>Enter product or service details below</DialogDescription></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div><Label>Product Name *</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Website Redesign" data-testid="product-name-input" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} placeholder="Brief description..." rows={3} /></div>

          {/* Image Upload */}
          <div>
            <Label className="flex items-center gap-1"><Image className="h-3.5 w-3.5" /> Images</Label>
            <div className="mt-1.5 space-y-2">
              {form.image_urls.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {form.image_urls.map((url, idx) => (
                    <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 group">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(idx)} className="absolute top-0 right-0 bg-red-500 text-white rounded-bl-md p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" id="product-img-upload" />
                <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => document.getElementById('product-img-upload').click()} disabled={uploading} data-testid="upload-product-image">
                  {uploading ? 'Uploading...' : <><Upload className="h-3.5 w-3.5" /> Add Images</>}
                </Button>
                <span className="text-xs text-gray-400">{form.image_urls.length} of 10 images</span>
              </div>
            </div>
          </div>

          {/* Video URL */}
          <div><Label className="flex items-center gap-1"><Video className="h-3.5 w-3.5" />Video URL</Label><Input value={form.video_url} onChange={e => setForm(p => ({...p, video_url: e.target.value}))} placeholder="https://youtube.com/watch?v=... or Google Drive share link" className="mt-1" data-testid="product-video-url" /><p className="text-[10px] text-gray-500 mt-1">Supports YouTube, Vimeo, Google Drive share links, and direct .mp4 URLs.</p></div>

          {/* Linked booking picker — appears only when category === "booking". Lets a
              product row point at a specific bookable service so shopping this product
              opens the booking widget directly. Fully bidirectional: creating a booking
              service auto-suggests a matching product row via the Booking page share. */}
          {form.category === 'booking' && (
            <div className="p-3 border-2 border-teal-200 bg-teal-50/40 rounded-lg space-y-2" data-testid="booking-link-section">
              <Label className="flex items-center gap-1 text-teal-800"><Calendar className="h-3.5 w-3.5" /> Linked Bookable Service</Label>
              {bookingServices.length === 0 ? (
                <p className="text-xs text-teal-700">No bookable services yet. Head to <span className="font-medium">Sales → Booking → Setup</span> to install a template, then come back.</p>
              ) : (
                <Select value={form.booking_service_id || 'none'} onValueChange={(v) => applyBookingService(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="booking-service-select"><SelectValue placeholder="Pick a bookable service…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (standalone booking product)</SelectItem>
                    {bookingServices.map((bs) => (
                      <SelectItem key={bs.id} value={bs.id}>{bs.name} · {bs.duration_min} min</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-[10px] text-teal-700">Selecting a linked service pre-fills the name, price, and description from Booking. Customers who buy this product are routed straight into the booking widget.</p>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label>Category</Label><Select value={form.category} onValueChange={v => setForm(p => ({...p, category: v}))}><SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Price ($)</Label><Input type="number" min="0" step="0.01" value={form.price} onChange={e => setForm(p => ({...p, price: e.target.value}))} placeholder="0.00" data-testid="product-price-input" /></div>
            <div><Label>Pricing Unit</Label><Select value={form.unit} onValueChange={v => setForm(p => ({...p, unit: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="each">Each</SelectItem><SelectItem value="project">Project</SelectItem><SelectItem value="hour">Hourly</SelectItem><SelectItem value="month">Monthly</SelectItem><SelectItem value="year">Annually</SelectItem></SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>SKU (optional)</Label><Input value={form.sku} onChange={e => setForm(p => ({...p, sku: e.target.value}))} placeholder="WEB-001" /></div>
            <div><Label>Tags (comma-separated)</Label><Input value={form.tags} onChange={e => setForm(p => ({...p, tags: e.target.value}))} placeholder="web, design" /></div>
          </div>
          <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => setForm(p => ({...p, active: v}))} /><Label>Active</Label></div>

          {/* Promotion Section */}
          <div className="border-t pt-3 mt-1">
            <div className="flex items-center gap-2 mb-2">
              <Switch checked={form.has_promo} onCheckedChange={v => setForm(p => ({...p, has_promo: v}))} data-testid="promo-toggle" />
              <Label className="flex items-center gap-1.5 text-sm font-medium">Promotion / Deal</Label>
            </div>
            {form.has_promo && (
              <div className="space-y-2 pl-1 animate-fadeIn">
                <div><Label className="text-xs">Promo Label</Label><Input value={form.promo_label} onChange={e => setForm(p => ({...p, promo_label: e.target.value}))} placeholder="e.g., Summer Sale, 50% Off" className="mt-0.5" data-testid="promo-label-input" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Discount Type</Label>
                    <Select value={form.promo_type} onValueChange={v => setForm(p => ({...p, promo_type: v}))}>
                      <SelectTrigger className="mt-0.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage Off (%)</SelectItem>
                        <SelectItem value="fixed_amount">Fixed Amount Off ($)</SelectItem>
                        <SelectItem value="buy_x_get_y">Buy X Get Y</SelectItem>
                        <SelectItem value="bundle">Bundle Deal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Discount Value</Label><Input type="number" min="0" step="0.01" value={form.promo_value} onChange={e => setForm(p => ({...p, promo_value: e.target.value}))} placeholder={form.promo_type === 'percentage' ? '20' : '50.00'} className="mt-0.5" data-testid="promo-value-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Start Date</Label><Input type="date" value={form.promo_start} onChange={e => setForm(p => ({...p, promo_start: e.target.value}))} className="mt-0.5" data-testid="promo-start-input" /></div>
                  <div><Label className="text-xs">End Date</Label><Input type="date" value={form.promo_end} onChange={e => setForm(p => ({...p, promo_end: e.target.value}))} className="mt-0.5" data-testid="promo-end-input" /></div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-500" data-testid="save-product-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Add Product'}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ─── Contact Preview Drawer (shows subscriber's contact info on catalog) ───
function ContactPreviewDrawer({ open, onClose, product, contactInfo }) {
  if (!open || !product) return null;

  const ch = contactInfo?.channels || {};
  const phone = contactInfo?.phone || '';
  const email = contactInfo?.email || '';
  const companyName = contactInfo?.business_name || 'Us';
  const voiceSlug = contactInfo?.voice_slug || null;
  const productName = product.name || '';

  const contactOptions = [
    phone && { id: 'call', label: 'Call', icon: PhoneCall, color: 'bg-emerald-600', url: `tel:${phone}` },
    (ch.whatsapp || phone) && { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'bg-[#25D366]', url: `https://wa.me/${(ch.whatsapp || phone).replace(/[^0-9+]/g, '')}?text=${encodeURIComponent(`Hi, I am interested in ${productName}. Can you share more details?`)}` },
    ch.messenger && { id: 'messenger', label: 'Messenger', icon: MessageCircle, color: 'bg-[#006AFF]', url: ch.messenger.startsWith('http') ? ch.messenger : `https://m.me/${ch.messenger}` },
    ch.telegram && { id: 'telegram', label: 'Telegram', icon: Send, color: 'bg-[#0088cc]', url: ch.telegram.startsWith('http') ? ch.telegram : `https://t.me/${ch.telegram.replace('@', '')}` },
    email && { id: 'email', label: 'Email', icon: Mail, color: 'bg-slate-600', url: `mailto:${email}?subject=Inquiry about ${encodeURIComponent(productName)}&body=${encodeURIComponent(`Hi,\n\nI am interested in ${productName}. Could you please share more details?\n\nThank you.`)}` },
    ch.instagram && { id: 'instagram', label: 'Instagram', icon: Instagram, color: 'bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737]', url: ch.instagram.startsWith('http') ? ch.instagram : `https://instagram.com/${ch.instagram.replace('@', '')}` },
    ch.facebook && { id: 'facebook', label: 'Facebook', icon: Facebook, color: 'bg-[#1877F2]', url: ch.facebook.startsWith('http') ? ch.facebook : `https://facebook.com/${ch.facebook}` },
    ch.linkedin && { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: 'bg-[#0A66C2]', url: ch.linkedin.startsWith('http') ? ch.linkedin : `https://linkedin.com/company/${ch.linkedin}` },
    ch.tiktok && { id: 'tiktok', label: 'TikTok', icon: Video, color: 'bg-black', url: ch.tiktok.startsWith('http') ? ch.tiktok : `https://tiktok.com/@${ch.tiktok.replace('@', '')}` },
    ch.website && { id: 'website', label: 'Website', icon: Globe, color: 'bg-slate-500', url: ch.website.startsWith('http') ? ch.website : `https://${ch.website}` },
    voiceSlug && { id: 'voice', label: 'AI Receptionist', icon: Headphones, color: 'bg-violet-600', url: `/voice/${voiceSlug}` },
  ].filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm bg-slate-950 border-slate-800 text-white" data-testid="contact-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white"><Phone className="h-5 w-5 text-emerald-400" />Contact {companyName}</DialogTitle>
          <DialogDescription className="text-slate-400">Reach out about &ldquo;{productName}&rdquo;</DialogDescription>
        </DialogHeader>
        {contactOptions.length > 0 ? (
          <div className="grid grid-cols-4 gap-3 mt-2">
            {contactOptions.map(opt => (
              <a
                key={opt.id}
                href={opt.url}
                target={opt.id === 'call' ? '_self' : '_blank'}
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1.5"
                data-testid={`catalog-contact-${opt.id}`}
              >
                <div className={cn("w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg transition-transform hover:scale-110", opt.color)}>
                  <opt.icon className="h-5 w-5" />
                </div>
                <span className="text-[10px] text-slate-400 text-center leading-tight">{opt.label}</span>
              </a>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-6">No contact channels configured yet. Go to Settings to add your contact info.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ─── Main Page ───
export default function ProductCatalogPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [filterPromo, setFilterPromo] = useState(false);
  const [view, setView] = useState('card');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const [contactItem, setContactItem] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);

  const fetchProducts = useCallback(async () => {
    try {
      const params = { active_only: false };
      if (search) params.search = search;
      if (filterCat && filterCat !== 'all') params.category = filterCat;
      const { data } = await axios.get(`${API}/api/sales/products`, { params });
      setProducts(Array.isArray(data) ? data : []);
    } catch { toast.error('Unable to load products'); setProducts([]); }
    finally { setLoading(false); }
  }, [search, filterCat]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Fetch subscriber contact channels for "Contact Us" on each product
  useEffect(() => {
    axios.get(`${API}/api/sales/contact-channels`)
      .then(r => {
        const data = r.data;
        setContactInfo({
          phone: data.phone || '',
          email: '', // Will be populated from user's own info
          business_name: '',
          channels: data,
          voice_slug: data.voice_slug || null,
        });
      })
      .catch(() => {});
    // Also get user info for email/business name
    axios.get(`${API}/api/auth/me`)
      .then(r => {
        setContactInfo(prev => ({
          ...prev,
          email: r.data?.email || '',
          business_name: r.data?.business_name || r.data?.full_name || '',
        }));
      })
      .catch(() => {});
  }, []);

  const handleSaved = (product, isUpdate) => {
    if (isUpdate) setProducts(prev => prev.map(p => p.id === product.id ? product : p));
    else setProducts(prev => [product, ...prev]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/api/sales/products/${id}`);
      setProducts(prev => prev.filter(p => p.id !== id));
      toast.success('Product removed successfully');
    } catch { toast.error('Unable to delete product'); }
  };

  const totalValue = (products || []).reduce((s, p) => s + (p.price || 0), 0);
  const activeCount = (products || []).filter(p => p.active).length;
  const promoCount = (products || []).filter(p => p.has_promo).length;
  const displayProducts = filterPromo ? products.filter(p => p.has_promo) : products;

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="product-catalog-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/20">
            <ShoppingBag className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Product Showcase<PageInfoTooltip pageKey="product-catalog" /></h1>
            <p className="text-xs text-gray-400">{products.length} items · {activeCount} active · {promoCount > 0 ? `${promoCount} promos · ` : ''}{formatCurrency(totalValue)} total</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 w-36 h-8 text-sm" data-testid="product-search" />
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-28 h-8 text-xs" data-testid="product-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <ViewToggle view={view} onChange={setView} />
          {promoCount > 0 && (
            <Button variant={filterPromo ? "default" : "outline"} size="sm" className={cn("h-8 text-xs gap-1", filterPromo && "bg-amber-500 hover:bg-amber-600")} onClick={() => setFilterPromo(!filterPromo)} data-testid="filter-promos-btn">
              <Tag className="h-3 w-3" />{filterPromo ? 'Promos' : 'Promos'}
            </Button>
          )}
          <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-emerald-600 hover:bg-emerald-500 h-8 gap-1 text-xs" data-testid="add-product-btn"><Plus className="h-3.5 w-3.5" /> Add</Button>
        </div>
      </div>

      {/* Product Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div>
      ) : displayProducts.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300 bg-slate-50/50">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-8 w-8 text-emerald-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-1">Create Your First Product</h3>
            <p className="text-sm text-gray-500 mb-5 max-w-sm mx-auto">Display your products with immersive previews, social sharing, and detailed analytics</p>
            <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-emerald-600 hover:bg-emerald-500 gap-1.5"><Plus className="h-4 w-4" /> Add Product</Button>
          </CardContent>
        </Card>
      ) : view === 'card' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayProducts.map(p => {
            const cat = CATEGORIES.find(c => c.value === p.category) || CATEGORIES[4];
            const hasMedia = (p.image_urls || []).length > 0 || p.video_url;
            return (
              <Card key={p.id} className="group border-gray-200 hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-500/5 transition-all duration-300 overflow-hidden" data-testid={`product-card-${p.id}`}>
                {/* Card media preview */}
                <div
                  className="relative h-44 bg-gradient-to-br from-slate-900 to-slate-800 overflow-hidden cursor-pointer"
                  onClick={() => setPreviewItem(p)}
                >
                  {hasMedia ? (
                    <img
                      src={(p.image_urls || [])[0] || ''}
                      alt={p.name}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                      style={{
                        maskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)',
                        WebkitMaskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)',
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="h-12 w-12 text-slate-700" />
                    </div>
                  )}
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center"><Eye className="h-5 w-5 text-white" /></div>
                  </div>
                  {/* Media count */}
                  {(p.image_urls || []).length > 1 && (
                    <div className="absolute top-2 right-2 bg-black/50 backdrop-blur-md rounded-md px-1.5 py-0.5 text-[10px] text-white/80">
                      {(p.image_urls || []).length} images
                    </div>
                  )}
                  {p.video_url && (
                    <div className="absolute top-2 left-2"><Badge className="bg-red-500/80 text-white text-[9px] gap-0.5"><Play className="h-2.5 w-2.5" />Video</Badge></div>
                  )}
                  {p.has_promo && (
                    <div className="absolute bottom-2 left-2"><Badge className="bg-amber-500 text-white text-[9px] gap-0.5 animate-pulse">{p.promo_label || (p.promo_type === 'percentage' ? `${p.promo_value}% OFF` : `${formatCurrency(p.promo_value)} OFF`)}</Badge></div>
                  )}
                  {!p.active && (
                    <div className="absolute top-2 left-2"><Badge className="bg-red-500/90 text-white text-[9px]">Inactive</Badge></div>
                  )}
                </div>

                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <Badge className={`${cat.color} text-[9px] mb-1`}>{cat.label}</Badge>
                      <h3 className="text-sm font-bold text-gray-900 truncate">{p.name}</h3>
                    </div>
                    <p className="text-base font-bold text-gray-900 ml-2 shrink-0">
                      {p.has_promo && p.promo_type === 'percentage' && p.promo_value > 0 ? (
                        <><span className="line-through text-gray-400 text-xs mr-1">{formatCurrency(p.price)}</span>{formatCurrency(p.price * (1 - p.promo_value / 100))}</>
                      ) : p.has_promo && p.promo_type === 'fixed_amount' && p.promo_value > 0 ? (
                        <><span className="line-through text-gray-400 text-xs mr-1">{formatCurrency(p.price)}</span>{formatCurrency(Math.max(0, p.price - p.promo_value))}</>
                      ) : (
                        formatCurrency(p.price)
                      )}
                      {formatUnitLabel(p.unit, true) && <span className="text-[10px] text-gray-400 font-normal">{formatUnitLabel(p.unit, true)}</span>}
                    </p>
                  </div>
                  {p.description && <p className="text-xs text-gray-500 line-clamp-2 mb-3">{p.description}</p>}
                  <div className="flex items-center gap-1.5">
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1 flex-1" onClick={() => setPreviewItem(p)} data-testid={`preview-product-${p.id}`}><Eye className="h-3 w-3" />Preview</Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border-emerald-200" onClick={() => setContactItem(p)} data-testid={`contact-product-${p.id}`}><Phone className="h-3 w-3" /></Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setShareItem(p)} data-testid={`share-product-${p.id}`}><Share2 className="h-3 w-3" /></Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => { setEditing(p); setModalOpen(true); }}><Pencil className="h-3 w-3" /></Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(p.id)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="border rounded-xl overflow-hidden bg-white overflow-x-auto">
          <div className="grid grid-cols-[2fr_1fr_1fr_0.8fr_150px] gap-2 px-4 py-2 bg-gray-50 text-[10px] font-semibold text-gray-500 uppercase border-b min-w-[700px]">
            <span>Product</span><span>Category</span><span>Price</span><span>Status</span><span className="text-right">Actions</span>
          </div>
          {displayProducts.map(p => {
            const cat = CATEGORIES.find(c => c.value === p.category) || CATEGORIES[4];
            return (
              <div key={p.id} className="grid grid-cols-[2fr_1fr_1fr_0.8fr_150px] gap-2 px-4 py-2.5 border-b hover:bg-emerald-50/30 items-center min-w-[700px] cursor-pointer" onClick={() => setPreviewItem(p)} data-testid={`product-row-${p.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {(p.image_urls || [])[0] ? (
                    <img src={p.image_urls[0]} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0"><Package className="h-5 w-5 text-gray-300" /></div>
                  )}
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{p.name}</h3>
                    {p.description && <p className="text-[10px] text-gray-400 truncate">{p.description}</p>}
                  </div>
                </div>
                <Badge className={`${cat.color} text-[9px] w-fit`}>{cat.label}</Badge>
                <span className="text-sm font-bold text-gray-800">{formatCurrency(p.price)}{formatUnitLabel(p.unit, true) && <span className="text-[10px] text-gray-400 font-normal">{formatUnitLabel(p.unit, true)}</span>}</span>
                <Badge className={p.active ? 'bg-green-100 text-green-700 text-[9px] w-fit' : 'bg-red-100 text-red-600 text-[9px] w-fit'}>{p.active ? 'Active' : 'Inactive'}</Badge>
                <div className="flex gap-1 justify-end" onClick={e => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setContactItem(p)} title="Contact"><Phone className="h-3.5 w-3.5 text-emerald-500" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShareItem(p)}><Share2 className="h-3.5 w-3.5 text-gray-400" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(p); setModalOpen(true); }}><Pencil className="h-3.5 w-3.5 text-gray-400" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(p.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ProductModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} />
      <ImmersivePreview open={!!previewItem} onClose={() => setPreviewItem(null)} product={previewItem} onShare={setShareItem} onContact={setContactItem} />
      <ShareModal open={!!shareItem} onClose={() => setShareItem(null)} product={shareItem} />
      <ContactPreviewDrawer open={!!contactItem} onClose={() => setContactItem(null)} product={contactItem} contactInfo={contactInfo} />
    </div>
  );
}
